import style from "./AddressDetails.module.css";
import { RootState } from "@/store/dtm";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import clsx from "clsx";
import { americanStateList, countryList, setClickInsightFormErrors, streetTypeList } from "@/lib/util";
import {
  setStreetNumber,
  setStreetName,
  setUnitNumber,
  setCity,
  setProvince,
  setPostalCode,
  setCountry,
  setMailingAddressSame,
  setStreetDirection,
  setStreetType,
  setUnitType,
} from "@/slices/homeAddressSlice";
import {
  setMailStreetNumber,
  setMailStreetName,
  setMailUnitNumber,
  setMailCity,
  setMailProvince,
  setMailPostalCode,
  setMailCountry,
  clearMailingAddressState,
} from "@/slices/mailingAddressSlice";
import { setResidentStatus, setResidenceYears, setResidenceMonths } from "@/slices/applicantSlice";
import { useNavigate } from "react-router-dom";
import { selectPreviousAddress } from "@/slices/PreviousAddressSlice";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createApiClient } from "@/lib/Requests";
import { AddressDto, ApplicationDto, AssetDto, DealAssetsDto, DealExpensesDto, FinancialAssetDto, LifeInsuranceDto, PropertyExpenseDto, RealEstateDto, VehicleDto } from "@/lib/DTMWebAPIClient.generated";
import { streetDirections } from "@/ts/streetDirections";
import { streetTypes } from "@/ts/streetTypes";
import { AnimatePresence, motion } from "framer-motion";
import { addAsset, assetInfo, clearAssetState, removeAsset, setAssetInfo } from "@/slices/assetSlice";
import { addExpense, clearExpenseState, expenseInfo, removeExpense, setExpenseInfo } from "@/slices/expenseSlice";
import { selectApplication } from "@/slices/applicationSlice";
import { applicationCategory } from "@/types/applicationType";
import dayjs from "dayjs";
import { clear } from "console";
import { setCachedApplicant, setCachedAssets } from "@/slices/sessionDataSlice";

export default function AddressDetails(props: any) {
  const [showEdit, setShowEdit] = useState(false);
  const [showAddress, setShowAddress] = useState(false);

  const [residenceYearsText, setResidenceYearsText] = useState("");
  const [residenceMonthsText, setResidenceMonthsText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [stateListType, setStateListType] = useState(1); //1 Canadian Provinces, 2 American States
  const credential = useSelector(
    (state: RootState) => state.credential
  );
  const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

  const displayAddressHistoryError = useRef(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getCountry = countryList();
  const getStreetTypes = streetTypeList();
  const getStates = americanStateList();

  const homeaddress = useSelector(
    (state: RootState) => state.homeAddress.address
  );
  const mailingAddress = useSelector(
    (state: RootState) => state.mailingAddress.address
  );
  const previousAddresses = useSelector(selectPreviousAddress).addresses;

  const mailingAddressSame = useSelector(
    (state: RootState) => state.homeAddress.mailingAddressSame
  );

  const isMailingAddressSame = useSelector(
    (state: RootState) => state.homeAddress.mailingAddressSame
  );
  const applicant = useSelector(
    (state: RootState) => state.applicant.applicant
  );
  const assets = useSelector((state: RootState) => state.assets.assets);
  const expenses = useSelector((state: RootState) => state.expenses.expenses);
  const application = useSelector(selectApplication).application;
  const sessionData = useSelector((state: RootState) => state.sessionData.currentSession);
  const [livingSituationChanged, setLivingSituationChanged] = useState(false);
  const [addressChanged, setAddressChanged] = useState(false);

  const methods = useForm({
    defaultValues: {
      autoAddress: homeaddress.unitNumber
        ? homeaddress.unitNumber + "-"
        : "" +
        homeaddress.streetNumber +
        " " +
        homeaddress.streetName +
        " " +
        homeaddress.streetType +
        ", " +
        homeaddress.city +
        ", " +
        homeaddress.province.slice(0, 2).toUpperCase() +
        " " +
        homeaddress.postalCode,
      streetNumber: homeaddress.streetNumber,
      streetName: homeaddress.streetName,
      streetType: homeaddress.streetType,
      streetDirection: homeaddress.streetDirection,
      unitNumber: homeaddress.unitNumber,
      unitType: homeaddress.unitType,
      cityName: homeaddress.city,
      provinceName: homeaddress.province,
      postalCode: homeaddress.postalCode,
      country: homeaddress.country,
      isMailingAddressSame: mailingAddressSame,
      maStreetNumber: mailingAddress.streetNumber,
      maStreetName: mailingAddress.streetName,
      maCityName: mailingAddress.city,
      maUnitNumber: mailingAddress.unitNumber,
      maProvinceName: mailingAddress.province,
      maPostalCode: mailingAddress.postalCode,
      maCountry: mailingAddress.country,
      residentStatus: "",
      residenceYears: "",
      residenceMonths: ""
    },
  });

  const [isMailAddressChecked, setIsMailAddressChecked] =
    useState(mailingAddressSame);

  const toggleMailingAddress = () => {
    setIsMailAddressChecked((current) => !current);
  };

  const fields = [
    {
      element: "search",
      field: "",
      mode: window.pca?.fieldMode.SEARCH,
    },

    {
      element: "span-street-address",
      field: "Line1",
      mode: window.pca?.fieldMode.POPULATE,
    },
    {
      element: "span-street-address2",
      field: "Line2",
      mode: window.pca?.fieldMode?.POPULATE,
    },
    { element: "city", field: "City", mode: window.pca?.fieldMode.POPULATE },
    {
      element: "span-state",
      field: "ProvinceCode",
      mode: window.pca?.fieldMode.POPULATE,
    },
    {
      element: "span-postcode",
      field: "PostalCode",
      mode: window.pca?.fieldMode.POPULATE,
    },
    {
      element: "span-country",
      field: "CountryName",
      mode: window.pca?.fieldMode.POPULATE,
    },
  ];
  const options = {
    key: "XD95-YY77-BP99-EM55",
  };

  const control = new window.pca.Address(fields, options);

  const openEdit = () => {
    setShowEdit(true);
    let unitNumberText = homeaddress.unitNumber ? homeaddress.unitNumber + "-" : ""
    methods.setValue(
      "autoAddress",
      unitNumberText +
      homeaddress.streetNumber +
      " " +
      homeaddress.streetName +
      " " +
      homeaddress.streetType +
      ", " +
      homeaddress.city +
      ", " +
      homeaddress.province.slice(0, 2).toUpperCase() +
      " " +
      homeaddress.postalCode
    );
    methods.setValue("streetNumber", homeaddress.streetNumber);
    methods.setValue("streetName", homeaddress.streetName);
    methods.setValue("streetType", homeaddress.streetType);
    methods.setValue("streetDirection", homeaddress.streetDirection);
    methods.setValue("unitType", homeaddress.unitType);
    methods.setValue("unitNumber", homeaddress.unitNumber);
    methods.setValue("cityName", homeaddress.city);
    if (homeaddress.country == "Canada") {
      methods.register("provinceName", {
        required: stateListType == 1 ? "Select your province name" : "Select your state",
      });
    }
    methods.setValue("provinceName", homeaddress.province);
    methods.setValue("postalCode", homeaddress.postalCode);
    methods.setValue("country", homeaddress.country);
    methods.setValue("isMailingAddressSame", mailingAddressSame);
    methods.setValue("maStreetNumber", mailingAddress.streetNumber);
    methods.setValue("maStreetName", mailingAddress.streetName);
    methods.setValue("maUnitNumber", mailingAddress.unitNumber);
    methods.setValue("maCityName", mailingAddress.city);
    methods.setValue("maProvinceName", mailingAddress.province);
    methods.setValue("maPostalCode", mailingAddress.postalCode);
    methods.setValue("maCountry", mailingAddress.country);
    methods.setValue("residentStatus", applicant.residentStatus);
    methods.setValue("residenceYears", applicant.residenceYears != 0 ? applicant.residenceYears.toString() : "0");
    setResidenceYearsText(applicant.residenceYears != 0 ? applicant.residenceYears.toString() : "0")
    methods.setValue("residenceMonths", applicant.residenceMonths != 0 ? applicant.residenceMonths.toString() : "0");
    setResidenceMonthsText(applicant.residenceMonths != 0 ? applicant.residenceMonths.toString() : "0")
    setIsMailAddressChecked(mailingAddressSame);
    props.editModeOn(!showEdit);
  };

  const closeEdit = (restoreAssets: boolean) => {
    if (restoreAssets) {
      dispatch(clearAssetState());
      dispatch(clearExpenseState());
      sessionData.cachedAssets.forEach((asset) => {
        dispatch(addAsset(asset));
      });
      sessionData.cachedExpenses.forEach((expense) => {
        dispatch(addExpense(expense));
      });
    }else{
      dispatch(setCachedAssets(assets));
      dispatch(setCachedApplicant(applicant));
    }
    setShowEdit(false);
    methods.clearErrors();
    methods.unregister([
      "streetNumber",
      "streetName",
      "streetDirection",
      "streetType",
      "unitType",
      "unitNumber",
      "cityName",
      "provinceName",
      "postalCode",
      "country",
      "isMailingAddressSame",
      "maStreetNumber",
      "maStreetName",
      "maCityName",
      "maProvinceName",
      "maPostalCode",
      "maCountry",
    ]);
    props.editModeOn(!showEdit);
  };

  const handleChange = (ev: any) => {
    setShowAddress(true);
    // checks for selecting an item from cp results
    try {
      if (ev.srcElement.parentElement.className.includes("pca")) {
        setTimeout(formatAddress, 500);
      }
    } catch (e) { }
  };

  const formatAddress = () => {
    const span = document.getElementById("span-street-address");
    const span2 = document.getElementById("span-city");
    const span3 = document.getElementById("span-state");
    const span4 = document.getElementById("span-postcode");
    const span5 = document.getElementById("span-country");

    if (!span || !span2 || !span3 || !span4 || !span5) return;

    const streetAddress = span!.innerHTML?.split(" ");
    const streetNumber = streetAddress?.shift();
    const streetInfo = streetAddress?.join(" ");
    let streetName = "";
    let streetDirection = "";
    let streetType = "";
    let streetComponents: string[] = streetInfo.split(" ");
    let lastStreetComponent = streetComponents[streetComponents.length - 1].toUpperCase();

    //Check if street direction and type exists in the street name
    if (streetDirections.includes(lastStreetComponent)) {
      streetDirection = lastStreetComponent;
      let secondLastStreetComponent = streetComponents[streetComponents.length - 2].toUpperCase();
      if (streetTypes.includes(secondLastStreetComponent)) {
        streetType = secondLastStreetComponent;
      }
      streetComponents.forEach((value, index) => {
        if (index < streetComponents.length - 2) {
          streetName += value + " "
        }
      });
    } else if (streetTypes.includes(lastStreetComponent)) {
      streetType = lastStreetComponent;
      streetComponents.forEach((value, index) => {
        if (index != streetComponents.length - 1) {
          streetName += value + " "
        }
      });
    } else {
      streetComponents.forEach((value, index) => {
        streetName += value + " "
      });
    }
    streetName = streetName.trim();

    methods.setValue("autoAddress", span!.innerHTML);
    methods.setValue("cityName", span2!.innerHTML);
    methods.setValue("provinceName", span3!.innerHTML);
    methods.setValue("postalCode", span4!.innerHTML);
    methods.setValue("country", span5!.innerHTML);
    methods.setValue("streetName", streetName);
    methods.setValue("streetType", streetType);
    methods.setValue("streetNumber", streetNumber!);
    methods.setValue("streetDirection", streetDirection);

    //Parse Unit Number (if it exists)
    if (streetNumber?.includes("-")) {
      const unit = streetNumber.split("-")[0];
      const number = streetNumber.split("-")[1];
      methods.setValue("unitNumber", unit!);
      methods.setValue("streetNumber", number!);
    } else {
      methods.setValue("unitNumber", "");
      methods.setValue("streetNumber", streetNumber!);
    }

    if (applicant.residentStatus == "own" && application.applicationCategory == applicationCategory.Buy) {
      updateRealEstateAddress();
    }
  };

  const setHomeAddress = async () => {
    dispatch(setStreetNumber(methods.getValues("streetNumber")));
    dispatch(setStreetName(methods.getValues("streetName")));
    dispatch(setStreetType(methods.getValues("streetType")));
    dispatch(setStreetDirection(methods.getValues("streetDirection")));
    dispatch(setUnitNumber(methods.getValues("unitNumber")));
    dispatch(setUnitType(methods.getValues("unitType")));
    dispatch(setCity(methods.getValues("cityName")));
    dispatch(setProvince(methods.getValues("provinceName")));
    dispatch(setPostalCode(methods.getValues("postalCode")));
    dispatch(setCountry(methods.getValues("country")));
    dispatch(setMailingAddressSame(methods.getValues("isMailingAddressSame")));
    dispatch(setResidentStatus(methods.getValues("residentStatus")));
    dispatch(setResidenceYears(+methods.getValues("residenceYears")));
    dispatch(setResidenceMonths(+methods.getValues("residenceMonths")));

    let totalOccupancyTime = (+methods.getValues("residenceYears") * 12) + +methods.getValues("residenceMonths");
    previousAddresses.forEach((address) => {
      totalOccupancyTime += (address.yearsLivedOn ? (address.yearsLivedOn * 12) : 0) + (address.monthsLivedOn ? address.monthsLivedOn : 0);
    });
    if (totalOccupancyTime < 36) {
      displayAddressHistoryError.current = true;
    } else {
      displayAddressHistoryError.current = false;
    }
    props.inAdequateAddressHistory(displayAddressHistoryError.current);

    setIsSaving(true);
    await client.apiApplicantsAddressPut(homeaddress.id, applicant.id, new AddressDto({
      id: homeaddress.id,
      streetNumber: methods.getValues("streetNumber"),
      streetName: methods.getValues("streetName"),
      streetType: methods.getValues("streetType"),
      streetDirection: methods.getValues("streetDirection"),
      unitType: methods.getValues("unitType"),
      unitNumber: methods.getValues("unitNumber"),
      city: methods.getValues("cityName"),
      region: methods.getValues("provinceName"),
      postalCode: methods.getValues("postalCode"),
      country: methods.getValues("country"),
      timeAtResidenceInMonths: (+methods.getValues("residenceYears") * 12) + +methods.getValues("residenceMonths"),
      residenceStatusType_Name: methods.getValues("residentStatus"),
      deliveryMode: undefined,
      modeNumber: undefined,
      deliveryMode2: undefined,
      modeNumber2: undefined,
      isMailing: false
    })).then((result) => {
      console.log("Trellis Address Updated");
    }).catch((error) => {
      console.log("Error updating trellis address");
    });
  };

  const setMailingAddress = () => {
    dispatch(setMailStreetNumber(methods.getValues("maStreetNumber")));
    dispatch(setMailStreetName(methods.getValues("maStreetName")));
    dispatch(setMailUnitNumber(methods.getValues("maUnitNumber")));
    dispatch(setMailCity(methods.getValues("maCityName")));
    dispatch(setMailProvince(methods.getValues("maProvinceName")));
    dispatch(setMailPostalCode(methods.getValues("maPostalCode")));
    dispatch(setMailCountry(methods.getValues("maCountry")));
  };

  const onError = (errors: object) => {
    setClickInsightFormErrors(errors);
  }

  const onSubmit = async (data: any) => {
    if (!isMailAddressChecked) {
      setMailingAddress();
    } else {
      dispatch(clearMailingAddressState());
    }
    if (data.streetType.trim() == "") {
      methods.setError("streetType", {
        type: "required",
        message: "Select your street type",
      });
      return;
    }

    await setHomeAddress();
    client.apiApplicationsFetch().then((result) => {
      result.applicant = applicant;
      result.homeAddress = homeaddress;
      client.apiApplicationsSave(new ApplicationDto({
        applicationData: JSON.stringify(result)
      })).catch((error) => {
        console.log("Error saving application info");
      });
    }).catch((error) => {
      console.log("Error fetching application info");
    });

    if (livingSituationChanged && methods.getValues("residentStatus") == "own") {
      setIsSaving(false);
      props.reviewAssets(true);
    } else if ((livingSituationChanged && methods.getValues("residentStatus") != "own") || addressChanged) {
      updateAssets();
    } else {
      setIsSaving(false);
      closeEdit(false);
    }
  };

  const updateAssets = async () => {
    let nonRegistered: FinancialAssetDto[] = [];
    let registered: FinancialAssetDto[] = [];
    let realEstate: RealEstateDto[] = [];
    let vehicle: VehicleDto[] = [];
    let lifeInsurance: LifeInsuranceDto[] = [];
    let other: AssetDto[] = [];
    let downPayment: AssetDto[] = [];

    assets.forEach((asset: assetInfo) => {
      switch (asset.assetCategory) {
        case "nonregistered":
          nonRegistered.push(new FinancialAssetDto({
            fiName: asset.details.nonRegistered?.financialInstitution,
            fiNameOther: asset.details.nonRegistered?.otherFinancialInstitution,
            id: asset.id,
            assetCategoryType_Name: asset.assetCategory,
            assetType_Name: asset.assetType == "term_gic" ? "termgic" : asset.assetType,
            origin_Name: "manual",
            value: asset.value,
            description: asset.description,
            downPaymentValue: asset.downPaymentValue,
            applicants: asset.applicants,
          }));
          break;
        case "registered":
          registered.push(new FinancialAssetDto({
            fiName: asset.details.registered?.financialInstitution,
            fiNameOther: asset.details.registered?.otherFinancialInstitution,
            id: asset.id,
            assetCategoryType_Name: asset.assetCategory,
            assetType_Name: asset.assetType,
            origin_Name: "manual",
            value: asset.value,
            description: asset.description,
            downPaymentValue: asset.downPaymentValue,
            applicants: asset.applicants,
          }));
          break;
        case "vehicle":
          vehicle.push(new VehicleDto({
            vehicleOwnershipType_Name: asset.details.vehicle?.ownership,
            year: asset.details.vehicle?.year ? +asset.details.vehicle.year : 0,
            make: asset.details.vehicle?.make,
            model: asset.details.vehicle?.model,
            id: asset.id,
            assetCategoryType_Name: asset.assetCategory,
            assetType_Name: asset.assetType,
            origin_Name: "manual",
            value: asset.value,
            description: asset.description,
            downPaymentValue: asset.downPaymentValue,
            applicants: asset.applicants,
          }));
          break;
        case "realestate":
          let originalDate = undefined;
          if (asset?.details?.realEstate?.originalPurchaseDate && asset?.details?.realEstate?.originalPurchaseDate.trim() != "") {
            let dateComponents = asset.details.realEstate.originalPurchaseDate.split("/");
            originalDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
          }
          let soldDate = undefined;
          if (asset?.details?.realEstate?.soldDate && asset?.details?.realEstate?.soldDate.trim() != "") {
            let dateComponents = asset.details.realEstate.soldDate.split("/");
            soldDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
          }
          realEstate.push(new RealEstateDto({
            occupancyType_Name: asset.details.realEstate?.occupancyType,
            propertyType_Name: asset.details.realEstate?.propertyType,
            rentalComponent: asset.details.realEstate?.rentalComponent === "yes",
            numberOfProperties: asset.details.realEstate?.numOfProperties ? +asset.details.realEstate?.numOfProperties : 0,
            assetAddress_StreetNumber: asset.details.realEstate?.address?.streetNumber,
            assetAddress_StreetName: asset.details.realEstate?.address?.streetName,
            assetAddress_StreetType: asset.details.realEstate?.address?.streetType,
            assetAddress_StreetDirection: asset.details.realEstate?.address?.streetDirection,
            assetAddress_UnitType: asset.details.realEstate?.address?.unitType,
            assetAddress_UnitNumber: asset.details.realEstate?.address?.unitNumber,
            assetAddress_City: asset.details.realEstate?.address?.city,
            assetAddress_PostalCode: asset.details.realEstate?.address?.postalCode,
            assetAddress_Region: asset.details.realEstate?.address?.province,
            assetAddress_Country: asset.details.realEstate?.address?.country,
            origPurchasePrice: asset.details.realEstate?.originalPurchasePrice,
            origPurchaseDate: originalDate,
            lot: asset.details.realEstate?.lot,
            block: asset.details.realEstate?.block,
            plan: asset.details.realEstate?.plan,
            section: asset.details.realEstate?.section,
            futureUsageType_Name: asset.details.realEstate?.futureUsage,
            hasPropertyBeenSold: asset.details.realEstate?.hasPropertyBeenSold === "yes",
            soldDate: soldDate,
            rentalIncome: asset.details.realEstate?.rentalIncome,
            id: asset.id,
            assetCategoryType_Name: asset.assetCategory,
            assetType_Name: asset.assetType,
            origin_Name: "manual",
            value: asset.value,
            description: asset.description,
            downPaymentValue: asset.downPaymentValue,
            applicants: asset.applicants,
          }));
          break;
        case "lifeinsurance":
          lifeInsurance.push(new LifeInsuranceDto({
            cashSurrenderValue: asset.details.lifeInsurance?.cashSurrenderValue,
            id: asset.id,
            assetCategoryType_Name: asset.assetCategory,
            assetType_Name: asset.assetType,
            origin_Name: "manual",
            value: asset.value,
            description: asset.description,
            downPaymentValue: asset.downPaymentValue,
            applicants: asset.applicants,
          }));
          break;
        case "other":
          other.push(new AssetDto({
            id: asset.id,
            assetCategoryType_Name: asset.assetCategory,
            assetType_Name: "other",
            origin_Name: "manual",
            value: asset.value,
            description: asset.description,
            downPaymentValue: asset.downPaymentValue,
            applicants: asset.applicants,
          }));
          break;
        default:
          downPayment.push(new AssetDto({
            id: asset.id,
            assetCategoryType_Name: "downpayment",
            assetType_Name: asset.assetType,
            origin_Name: "manual",
            value: 0,
            description: asset.description,
            downPaymentValue: asset.value,
            applicants: asset.applicants,
          }));
          break
      }
    });

    await client.apiAssetsPost(new DealAssetsDto({
      nonRegistered: nonRegistered,
      registered: registered,
      realEstate: realEstate,
      vehicle: vehicle,
      lifeInsurance: lifeInsurance,
      other: other,
      downPayment: downPayment,
    })).then(async (result: DealAssetsDto) => {
      // Handle the result if needed
      setIsSaving(false);
      if (result.nonRegistered && result.nonRegistered.length > 0) {
        result.nonRegistered.forEach((asset: FinancialAssetDto) => {
          assets.forEach((a: assetInfo) => {
            if (a.details.nonRegistered && a.details.nonRegistered.financialInstitution === asset.fiName &&
              a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              newAsset.id = asset.id ? asset.id : 0;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
      }
      if (result.registered && result.registered.length > 0) {
        result.registered.forEach((asset: FinancialAssetDto) => {
          assets.forEach((a: assetInfo) => {
            if (a.details.registered && a.details.registered.financialInstitution === asset.fiName &&
              a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              newAsset.id = asset.id ? asset.id : 0;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
      }
      if (result.realEstate && result.realEstate.length > 0) {
        let expenseList: PropertyExpenseDto[] = [];
        result.realEstate.forEach(async (asset: RealEstateDto) => {

          let existingExpense = expenses.find(exp => exp.assetId === asset.id);
          let existingAsset = assets.find(a => a.details.realEstate && a.details.realEstate.occupancyType === asset.occupancyType_Name &&
            a.value === asset.value && a.downPaymentValue === asset.downPaymentValue);

          //Append Property Tax as Expense for Real Estate Asset
          let propertyExpenseDto = new PropertyExpenseDto({
            assetId: asset.id ? asset.id : 0,
            expenseCategoryType: "property",
            taxAmount: existingAsset && existingAsset.details.realEstate && existingAsset.details.realEstate.propertyTax ? existingAsset.details.realEstate.propertyTax : 0,
            taxFrequency: "annually",
            condoFeesAmount: existingAsset && existingAsset.details.realEstate && existingAsset.details.realEstate.maintenanceFee ? existingAsset.details.realEstate.maintenanceFee : 0,
            condoFeesFrequency: "monthly",
            utilityFeesAmount: existingAsset && existingAsset.details.realEstate && existingAsset.details.realEstate.heatingCost ? existingAsset.details.realEstate.heatingCost : 0,
            utilityFeesFrequency: "monthly",
            id: existingExpense ? existingExpense.id : 0,
            applicants: realEstate[0].applicants
          });
          expenseList.push(propertyExpenseDto);

          assets.forEach((a: assetInfo) => {
            if (a.details.realEstate && a.details.realEstate.occupancyType === asset.occupancyType_Name &&
              a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              let newDetails = { ...newAsset.details };
              newAsset.id = asset.id ? asset.id : 0;
              if (newAsset.details.realEstate?.address.streetNumber === homeaddress.streetNumber &&
                newAsset.details.realEstate?.address.streetName === homeaddress.streetName &&
                newAsset.details.realEstate?.address.streetType === homeaddress.streetType &&
                newAsset.details.realEstate?.address.city === homeaddress.city) {
                let realEstateDetails = { ...newDetails.realEstate };
                newDetails.realEstate = {
                  occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                  propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                  rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                  numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                  ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                  address: {
                    displayId: newAsset.displayId,
                    streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                    streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                    streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                    streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                    unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                    unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                    city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                    province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                    postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                    country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                  },
                  originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                  originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                  originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                  propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                  propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                  heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                  heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                  maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                  maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                  lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                  block: realEstateDetails.block ? realEstateDetails.block : "",
                  plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                  section: realEstateDetails.section ? realEstateDetails.section : "",
                  futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                  hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                  soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                  rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                  rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
                  manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                  isCurrentResidence: true
                }
              }
              newAsset.details = newDetails;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
        await client.apiExpensesPost(new DealExpensesDto({
          property: expenseList,
        })).then((expenseResult) => {
          if (expenseResult.property && expenseResult.property.length > 0) {
            expenseResult.property.forEach((expense: PropertyExpenseDto) => {
              let existingExpense = expenses.find(exp => exp.id === expense.id);
              if (existingExpense == undefined) {
                let newExpense: expenseInfo = {
                  displayId: (expenses.length + 1).toString(),
                  id: expense.id ? expense.id : 0,
                  assetId: expense.assetId ? expense.assetId : 0,
                  expenseCategory: "property",
                  propertyTax: expense.taxAmount ? expense.taxAmount : 0,
                  heatingCost: expense.utilityFeesAmount ? expense.utilityFeesAmount : 0,
                  maintenanceFee: expense.condoFeesAmount ? expense.condoFeesAmount : 0,
                  ownershipPercentage: expense.ownershipPercentage ? expense.ownershipPercentage : 0,
                  applicants: expense.applicants ? expense.applicants : [],
                };
                dispatch(addExpense(newExpense));
              } else {
                let updateExpense = { ...existingExpense };
                updateExpense.propertyTax = expense.taxAmount ? expense.taxAmount : 0;
                updateExpense.heatingCost = expense.utilityFeesAmount ? expense.utilityFeesAmount : 0;
                updateExpense.maintenanceFee = expense.condoFeesAmount ? expense.condoFeesAmount : 0;
                dispatch(setExpenseInfo(updateExpense));
              }
            });
            //Remove expenses that are no longer linked to any assets
            let removableExpenses: number[] = [];
            expenses.forEach(exp => {
              let validExpense = false;
              expenseResult.property?.forEach((expense: PropertyExpenseDto) => {
                if (exp.id === expense.id) {
                  validExpense = true;
                }
              });
              if (!validExpense) {
                removableExpenses.push(exp.id);
              }
            });
            removableExpenses.forEach(id => {
              dispatch(removeExpense(id));
            });
          }

        }).catch((error) => {
          console.log("Error saving property expense for asset");
        });
      }
      if (result.vehicle && result.vehicle.length > 0) {
        result.vehicle.forEach((asset: VehicleDto) => {
          assets.forEach((a: assetInfo) => {
            if (a.details.vehicle && a.details.vehicle.ownership === asset.vehicleOwnershipType_Name && a.details.vehicle.make === asset.make &&
              a.details.vehicle.model === asset.model && a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              newAsset.id = asset.id ? asset.id : 0;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
      }
      if (result.lifeInsurance && result.lifeInsurance.length > 0) {
        result.lifeInsurance.forEach((asset: LifeInsuranceDto) => {
          assets.forEach((a: assetInfo) => {
            if (a.details.lifeInsurance && a.details.lifeInsurance.cashSurrenderValue === asset.cashSurrenderValue &&
              a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              newAsset.id = asset.id ? asset.id : 0;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
      }
      if (result.downPayment && result.downPayment.length > 0) {
        result.downPayment.forEach((asset: AssetDto) => {
          assets.forEach((a: assetInfo) => {
            if (a.assetType === asset.assetType_Name && a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              newAsset.id = asset.id ? asset.id : 0;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
      }
      if (result.other && result.other.length > 0) {
        result.other.forEach((asset: AssetDto) => {
          assets.forEach((a: assetInfo) => {
            if (a.assetCategory === asset.assetCategoryType_Name && a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              newAsset.id = asset.id ? asset.id : 0;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
      }
      closeEdit(false);
    }).catch((error) => {
      closeEdit(false);
    });
  }

  useEffect(() => {
    //Check if address history is less than three years
    let totalOccupancyTime = (applicant.residenceYears * 12) + (applicant.residenceMonths);
    previousAddresses.forEach((address) => {
      totalOccupancyTime += (address.yearsLivedOn ? (address.yearsLivedOn * 12) : 0) + (address.monthsLivedOn ? address.monthsLivedOn : 0);
    });

    if (totalOccupancyTime < 36) {
      displayAddressHistoryError.current = true;
      props.inAdequateAddressHistory(displayAddressHistoryError.current);
    } else {
      displayAddressHistoryError.current = false;
    }
  }, []);

  const residenceYearsChangeHandler = (e: any) => {
    setResidenceYearsText(e.target.value);
    methods.clearErrors("residenceYears");
    methods.setValue("residenceYears", e.target.value);
  }

  const residenceMonthsChangeHandler = (e: any) => {
    setResidenceMonthsText(e.target.value);
    methods.clearErrors("residenceMonths");
    methods.setValue("residenceMonths", e.target.value);
  }

  const residentStatusChangeHandler = (e: any) => {
    setLivingSituationChanged(true);
    methods.setValue("residentStatus", e.target.value);

    if (application.applicationCategory == applicationCategory.Buy) {
      if (e.target.value == "own") {
        let assetId = (assets.length + 1).toString();

        //Auto hide other assets (if they're visible)
        assets.forEach(a => {
          if (a.displayId != assetId) {
            let newAssetInfo = { ...a }
            newAssetInfo.visible = false;
            dispatch(setAssetInfo(newAssetInfo));
          }
        });

        let newAssetInfo: assetInfo = {
          displayId: assetId,
          id: 0,
          visible: true,
          assetCategory: "realestate",
          assetType: "",
          origin: "",
          value: 0,
          valueText: "",
          description: "",
          downPaymentValue: 0,
          downPaymentValueText: "",
          applicants: [applicant.id],
          details: {
            nonRegistered: undefined,
            registered: undefined,
            realEstate: {
              occupancyType: "primary",
              propertyType: "",
              rentalComponent: "",
              numOfProperties: "",
              ownershipPercentage: "",
              address: {
                displayId: assetId,
                streetNumber: methods.getValues("streetNumber"),
                streetName: methods.getValues("streetName"),
                streetType: methods.getValues("streetType"),
                streetDirection: methods.getValues("streetDirection"),
                unitType: methods.getValues("unitType"),
                unitNumber: methods.getValues("unitNumber"),
                city: methods.getValues("cityName"),
                province: methods.getValues("provinceName"),
                postalCode: methods.getValues("postalCode"),
                country: methods.getValues("country")
              },
              originalPurchasePrice: 0,
              originalPurchasePriceText: "",
              originalPurchaseDate: "",
              propertyTax: 0,
              propertyTaxText: "",
              heatingCost: 0,
              heatingCostText: "",
              maintenanceFee: 0,
              maintenanceFeeText: "",
              lot: "",
              block: "",
              plan: "",
              section: "",
              futureUsage: "",
              hasPropertyBeenSold: "",
              soldDate: "",
              rentalIncome: 0,
              rentalIncomeText: "",
              manualAddressEntered: false,
              isCurrentResidence: true
            },
            vehicle: undefined,
            lifeInsurance: undefined
          }
        };
        dispatch(addAsset(newAssetInfo));
      } else {
        //Remove own real estate asset if it exists
        assets.forEach(a => {
          if (a.assetCategory == "realestate" && a.details.realEstate && a.details.realEstate.isCurrentResidence) {
            dispatch(removeAsset(a.displayId));

            //Remove expense linked to this asset
            expenses.forEach(exp => {
              if (exp.assetId == a.id) {
                dispatch(removeExpense(exp.id));
              }
            });
          }
        });
      }
    }
  }

  const handleCaChange = (ev: any) => {
    if (ev.target.value === "Canada") {
      setStateListType(1);
      methods.register("provinceName", {
        required: "Select your province name",
      });
      methods.register("postalCode", {
        required: "Enter your postal code",
      });
    } else if (ev.target.value === "United States") {
      setStateListType(2);
      methods.register("provinceName", {
        required: "Select your state name",
      });
      methods.register("postalCode", {
        required: "Enter your zip code",
      });
    } else {
      setStateListType(0);
      methods.unregister("provinceName");
      methods.register("postalCode", {
        required: "Enter your zip.",
      });
    }
  };

  const updateRealEstateAddress = () => {
    assets.forEach((asset) => {
      if (asset.assetCategory == "realestate" && asset.details?.realEstate?.isCurrentResidence) {
        //Update address if it was edited by user
        let newAssetInfo: assetInfo = { ...asset }
        let newDetails = { ...newAssetInfo.details };
        let realEstateDetails = { ...newAssetInfo.details.realEstate };
        realEstateDetails.address = {
          displayId: asset.displayId,
          streetNumber: methods.getValues("streetNumber"),
          streetName: methods.getValues("streetName"),
          streetType: methods.getValues("streetType"),
          streetDirection: methods.getValues("streetDirection"),
          city: methods.getValues("cityName"),
          province: methods.getValues("provinceName"),
          country: methods.getValues("country"),
          postalCode: methods.getValues("postalCode"),
          unitType: methods.getValues("unitType"),
          unitNumber: methods.getValues("unitNumber")
        }
        newDetails.realEstate = {
          occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
          propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
          rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
          numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
          ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
          address: {
            displayId: asset.displayId,
            streetNumber: methods.getValues("streetNumber") ? methods.getValues("streetNumber") : "",
            streetName: methods.getValues("streetName") ? methods.getValues("streetName") : "",
            streetType: methods.getValues("streetType") ? methods.getValues("streetType") : "",
            streetDirection: methods.getValues("streetDirection") ? methods.getValues("streetDirection") : "",
            unitType: methods.getValues("unitType") ? methods.getValues("unitType") : "",
            unitNumber: methods.getValues("unitNumber") ? methods.getValues("unitNumber") : "",
            city: methods.getValues("cityName") ? methods.getValues("cityName") : "",
            province: methods.getValues("provinceName") ? methods.getValues("provinceName") : "",
            postalCode: methods.getValues("postalCode") ? methods.getValues("postalCode") : "",
            country: methods.getValues("country") ? methods.getValues("country") : ""
          },
          originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
          originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
          originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
          propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
          propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
          heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
          heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
          maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
          maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
          lot: realEstateDetails.lot ? realEstateDetails.lot : "",
          block: realEstateDetails.block ? realEstateDetails.block : "",
          plan: realEstateDetails.plan ? realEstateDetails.plan : "",
          section: realEstateDetails.section ? realEstateDetails.section : "",
          futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
          hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
          soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
          rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
          rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
          manualAddressEntered: false,
          isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
        }
        newAssetInfo.details = newDetails;
        dispatch(setAssetInfo(newAssetInfo));
        setAddressChanged(true);
      }
    });
  }

  return (
    <>
      <hr className="mt-5" />
      <div className="flex flex-row justify-between mt-5">
        <h2>Address</h2>
        {!showEdit && (
          <h3
            className="brand-blue-text text-xs grid grid-flow-col cursor-pointer"
            onClick={openEdit}
          >
            <img
              src="assets/edit-icon.svg"
              className={style.editIcon}
              alt="edit"
            />
            <span>Edit</span>
          </h3>
        )}
      </div>
      <div className={style.addContainer}>
        <AnimatePresence mode="wait" initial={false}>
          {!showEdit ? (
            <motion.div
              key="6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onAnimationComplete={() => {
                document.addEventListener("click", handleChange);
                return () => window.removeEventListener("click", handleChange);
              }}>
              <div className="flex md:flex-row flex-col">
                <div className={style.twoColFirst}>
                  <p className="mt-5">Home address</p>
                  <p className={style.addressDetails + " mt-3"}>
                    {homeaddress?.unitNumber ? homeaddress?.unitNumber + "-" : ""}
                    {homeaddress.streetNumber} {homeaddress.streetName} {homeaddress.streetType},{" "}
                  </p>
                  <p className={style.addressDetails}>
                    {homeaddress.city} {homeaddress.province}{" "}
                    {homeaddress.postalCode}
                  </p>
                  <p className={style.addressDetails}>{homeaddress.country}</p>
                </div>
              </div>
              <div className="flex md:flex-row flex-col">
                <div className={style.twoColFirst}>
                  <p className="mt-5">Living Situation</p>
                  <p className={style.addressDetails + " mt-3"}>
                    {applicant.residentStatus == "own" ? "Owning"
                      : applicant.residentStatus == "rent" ? "Renting"
                        : applicant.residentStatus == "liveswithparents" ? "Living with Parents" : "Boarding"}
                  </p>
                </div>
                <div>
                  <p className="mt-5 text-sm">How long have you lived at this address?</p>
                  {isMailingAddressSame ? (
                    <p className={style.addressDetails + " mt-3"}>
                      {applicant.residenceYears > 0 && applicant.residenceYears + " Year" + (applicant.residenceYears != 1 ? "s" : "")}
                      {applicant.residenceYears > 0 && applicant.residenceMonths > 0 && " & "}
                      {applicant.residenceMonths > 0 && applicant.residenceMonths + " Month" + (applicant.residenceMonths != 1 ? "s" : "")}
                    </p>
                  ) : (
                    <>
                      <p className={style.addressDetails + " mt-3"}>
                        {mailingAddress?.unitNumber
                          ? mailingAddress?.unitNumber + "-"
                          : ""}
                        {mailingAddress.streetNumber} {mailingAddress.streetName},{" "}
                      </p>
                      <p className={style.addressDetails}>
                        {mailingAddress.city} {mailingAddress.province}{" "}
                        {mailingAddress.postalCode}
                      </p>
                      <p className={style.addressDetails}>{mailingAddress.country}</p>
                    </>
                  )}
                </div>
              </div>
              {displayAddressHistoryError.current &&
                <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                  <img src="assets/error-info-icon.svg" alt="error" />
                  <span className="text-sm ml-2 mt-1">
                    Three years of address history is required
                    <span className="underline cursor-pointer ml-5" onClick={() => {
                      navigate("/single/previousaddress", { state: { editMode: true } });
                    }} style={{ color: "var(--error-red)" }}>Add more addresses</span>
                  </span>
                </div>
              }
            </motion.div>
          ) : (
            <motion.div
              key="7"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}>
              <form onSubmit={methods.handleSubmit(onSubmit, onError)} className="mt-5">
                <label htmlFor="search">Search for your address</label>
                <div className="flex relative">
                  <span>
                    <img
                      src="assets/magnifying-glass-icon.svg"
                      className={clsx(
                        methods.formState?.errors?.autoAddress ? "hidden" : "",
                        "w-5 h-5 absolute top-5",
                        style.imageIcon
                      )}
                      alt="search"
                      height={5}
                      width={5}
                    />
                    <img
                      src="assets/error-icon.svg"
                      className={clsx(
                        methods.formState?.errors?.autoAddress ? "" : "hidden",
                        "w-4 h-4 absolute top-6"
                      )}
                      alt="error"
                      height={5}
                      width={5}
                    />
                  </span>
                  <input
                    autoComplete="off"
                    className={clsx(
                      methods.formState?.errors?.autoAddress?.message
                        ? "error"
                        : "",
                      "mt-2",
                      style.autoAddress
                    )}
                    id="search"
                    type="text"
                    placeholder="Start typing address"
                    {...methods.register("autoAddress")}
                  />
                </div>
                <div className={clsx("ml-2 mt-2", showAddress ? "" : "hidden")}>
                  <p id="formatted-address"></p>
                  <span id="span-street-address"></span>
                  <br />
                  <span id="span-city"></span>
                  &nbsp;
                  <span id="span-state"></span>&nbsp;
                  <span id="span-postcode"></span>
                  <br />
                  <span id="span-country"></span>
                </div>
                <div className="flex lg:flex-row flex-col">
                  <div className={style.twoColFirst}>
                    <p className="mt-5 text-sm">Street number</p>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.streetNumber
                              ? ""
                              : "hidden",
                            "w-4 h-4 absolute top-6",
                            style.errorIcon
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <input
                        className={clsx(
                          methods.formState?.errors?.streetNumber?.message
                            ? "error"
                            : "",
                          "mt-2",
                          style.addInput
                        )}
                        type="text"
                        id="streetNumber"
                        maxLength={6}
                        {...methods.register("streetNumber", {
                          required: "Enter your street number",
                          pattern: {
                            value: /^[ #a-zA-Z0-9._-]+$/,
                            message: "Enter a valid street number",
                          },
                        })}
                        onBlur={(e) => {
                          //Update address of real estate asset if living situation is own
                          if (applicant.residentStatus == "own" && application.applicationCategory == applicationCategory.Buy) {
                            updateRealEstateAddress();
                          }
                        }}
                      />
                    </div>
                    {methods.formState?.errors?.streetNumber?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.streetNumber.message}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="mt-5 text-sm">Street name</p>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.streetName
                              ? ""
                              : "hidden",
                            "w-4 h-4 absolute top-6",
                            style.errorIcon
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <input
                        className={clsx(
                          methods.formState?.errors?.streetName?.message
                            ? "error"
                            : "",
                          "mt-2",
                          style.addInput
                        )}
                        type="text"
                        id="streetName"
                        maxLength={23}
                        {...methods.register("streetName", {
                          required: "Enter your street name",
                          pattern: {
                            value: /^[ #a-zA-ZÀ-Ÿ0-9._-]+$/,
                            message: "Enter a valid street name",
                          },
                        })}
                        onBlur={(e) => {
                          //Update address of real estate asset if living situation is own
                          if (applicant.residentStatus == "own" && application.applicationCategory == applicationCategory.Buy) {
                            updateRealEstateAddress();
                          }
                        }}
                      />
                    </div>
                    {methods.formState?.errors?.streetName?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.streetName.message}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex lg:flex-row flex-col">
                  <div className={style.twoColFirst}>
                    <p className="mt-5 text-sm">Street Type</p>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.streetType
                              ? ""
                              : "hidden",
                            "w-4 h-4 absolute top-6",
                            style.errorIcon
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <select
                        id="streetType"
                        {...methods.register("streetType")}
                        className={clsx(
                          methods.formState?.errors?.streetType?.message
                            ? "error"
                            : "",
                          "mt-2 pt-2.5",
                          style.addInput
                        )}
                        onChange={(e) => {
                          methods.setValue("streetType", e.target.value);
                          methods.clearErrors("streetType");
                        }}
                        onBlur={(e) => {
                          //Update address of real estate asset if living situation is own
                          if (applicant.residentStatus == "own" && application.applicationCategory == applicationCategory.Buy) {
                            updateRealEstateAddress();
                          }
                        }}
                      >
                        <option hidden value="">
                          Street Type
                        </option>
                        <option value=" "></option>
                        {getStreetTypes}
                      </select>
                    </div>
                    {methods.formState?.errors?.streetType?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.streetType.message}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="mt-5 text-sm">Street Direction (optional)</p>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.streetDirection
                              ? ""
                              : "hidden",
                            "w-4 h-4 absolute top-6",
                            style.errorIcon
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <select
                        id="streetDirection"
                        {...methods.register("streetDirection")}
                        className={clsx(
                          methods.formState?.errors?.streetDirection?.message
                            ? "error"
                            : "",
                          "mt-2 pt-2.5",
                          style.addInput
                        )}
                        onChange={(e) => {
                          methods.setValue("streetDirection", e.target.value);
                          methods.clearErrors("streetDirection");
                        }}
                        onBlur={(e) => {
                          //Update address of real estate asset if living situation is own
                          if (applicant.residentStatus == "own" && application.applicationCategory == applicationCategory.Buy) {
                            updateRealEstateAddress();
                          }
                        }}
                      >
                        <option hidden value="">Direction</option>
                        <option value=" "></option>
                        <option value="S">S</option>
                        <option value="SE">SE</option>
                        <option value="N">N</option>
                        <option value="NW">NW</option>
                        <option value="W">W</option>
                        <option value="SW">SW</option>
                        <option value="NE">NE</option>
                        <option value="E">E</option>
                      </select>
                    </div>
                    {methods.formState?.errors?.streetDirection?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.streetDirection.message}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex lg:flex-row flex-col">
                  <div className={style.twoColFirst}>
                    <p className="mt-5 text-sm">Unit Type (optional)</p>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.unitType
                              ? ""
                              : "hidden",
                            "w-4 h-4 absolute top-6",
                            style.errorIcon
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <select
                        id="unitType"
                        {...methods.register("unitType")}
                        className={clsx(
                          methods.formState?.errors?.unitType?.message
                            ? "error"
                            : "",
                          "mt-2 pt-2.5",
                          style.addInput
                        )}
                        onChange={(e) => {
                          methods.setValue("unitType", e.target.value);
                          if (e.target.value.trim() == "") {
                            methods.unregister("unitNumber");
                          } else {
                            methods.register("unitNumber", {
                              required: "Enter your unit number",
                              pattern: {
                                value: /^[ #a-zA-Z0-9._-]+$/,
                                message: " Please enter a valid unit number",
                              },
                            });
                          }
                        }}
                        onBlur={(e) => {
                          //Update address of real estate asset if living situation is own
                          if (applicant.residentStatus == "own" && application.applicationCategory == applicationCategory.Buy) {
                            updateRealEstateAddress();
                          }
                        }}
                      >
                        <option hidden value=""> Unit Type</option>
                        <option value=" "></option>
                        <option value="apt"> APT</option>
                        <option value="unit"> UNIT</option>
                        <option value="suite"> SUITE</option>
                      </select>
                    </div>
                    {methods.formState?.errors?.unitType?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState?.errors?.unitType?.message.toString()}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="mt-5 text-sm">Unit number (if applicable)</p>
                    <div className="flex relative">
                      <input
                        className={clsx("mt-2", style.addInput)}
                        type="text"
                        id="unitNumber"
                        maxLength={5}
                        {...methods.register("unitNumber", {
                          pattern: {
                            value: /^[ #a-zA-Z0-9._-]+$/,
                            message:
                              "Enter a valid unit number. Only letters, numbers and _-.# are accepted",
                          },
                        })}
                        onChange={(e) => {
                          methods.clearErrors("unitNumber");
                        }}
                        onBlur={(e) => {
                          //Update address of real estate asset if living situation is own
                          if (applicant.residentStatus == "own" && application.applicationCategory == applicationCategory.Buy) {
                            updateRealEstateAddress();
                          }
                        }}
                      />
                    </div>
                    {methods.formState?.errors?.unitNumber?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1" style={{ whiteSpace: "pre-line" }}>
                        {methods.formState?.errors?.unitNumber?.message?.toString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex lg:flex-row flex-col">
                  <div className={style.twoColFirst}>
                    <p className="mt-5 text-sm">City</p>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.cityName
                              ? ""
                              : "hidden",
                            "w-4 h-4 absolute top-6",
                            style.errorIcon
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <input
                        className={clsx(
                          methods.formState?.errors?.cityName?.message
                            ? "error"
                            : "",
                          "mt-2",
                          style.addInput
                        )}
                        type="text"
                        id="cityName"
                        maxLength={40}
                        {...methods.register("cityName", {
                          required: "Enter your city name",
                          pattern: {
                            value: /^[ #a-zA-ZÀ-Ÿ0-9._'-]+$/,
                            message: "Enter a valid city name",
                          },
                        })}
                        onBlur={(e) => {
                          //Update address of real estate asset if living situation is own
                          if (applicant.residentStatus == "own" && application.applicationCategory == applicationCategory.Buy) {
                            updateRealEstateAddress();
                          }
                        }}
                      />
                    </div>
                    {methods.formState?.errors?.cityName?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.cityName.message}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="mt-5 text-sm">Province</p>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.provinceName
                              ? ""
                              : "hidden",
                            "w-4 h-4 absolute top-6",
                            style.errorIcon
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      {stateListType === 1 || stateListType == 2 ?
                        <select
                          {...methods.register("provinceName")}
                          onBlur={(e) => {
                            //Update address of real estate asset if living situation is own
                            if (applicant.residentStatus == "own" && application.applicationCategory == applicationCategory.Buy) {
                              updateRealEstateAddress();
                            }
                          }}
                          className={clsx(
                            methods.formState?.errors?.provinceName?.message
                              ? "error"
                              : "",
                            "mt-2 pt-2.5",
                            style.addInput
                          )}
                          id="province"
                        >
                          <option hidden value="">
                            {stateListType === 1 && "Province"}
                            {stateListType === 2 && "State"}
                          </option>
                          {stateListType === 1 && <option value="AB">Alberta</option>}
                          {stateListType === 1 && <option value="BC">British Columbia</option>}
                          {stateListType === 1 && <option value="MB">Manitoba</option>}
                          {stateListType === 1 && <option value="NB">New Brunswick</option>}
                          {stateListType === 1 && <option value="NL">Newfoundland and Labrador</option>}
                          {stateListType === 1 && <option value="NS">Nova Scotia</option>}
                          {stateListType === 1 && <option value="ON">Ontario</option>}
                          {stateListType === 1 && <option value="PE">Prince Edward Island</option>}
                          {stateListType === 1 && <option value="QC">Quebec</option>}
                          {stateListType === 1 && <option value="SK">Saskatchewan</option>}
                          {stateListType === 1 && <option value="NT">Northwest Territories</option>}
                          {stateListType === 1 && <option value="NU">Nunavut</option>}
                          {stateListType === 1 && <option value="YT">Yukon</option>}

                          {stateListType === 2 && getStates}
                        </select>
                        : <input
                          id="province"
                          type="text"
                          className={clsx(
                            methods.formState?.errors?.provinceName?.message
                              ? "error"
                              : "",
                            "mt-2",
                            style.addInput
                          )}
                          value={methods.getValues("provinceName")}
                          maxLength={4}
                          onChange={(e) => {
                            methods.setValue("provinceName", e.target.value);
                            methods.clearErrors("provinceName");
                          }}
                          onBlur={(e) => {
                            //Update address of real estate asset if living situation is own
                            if (applicant.residentStatus == "own" && application.applicationCategory == applicationCategory.Buy) {
                              updateRealEstateAddress();
                            }
                          }}
                        />
                      }
                    </div>
                    {methods.formState?.errors?.provinceName?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.provinceName.message}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex lg:flex-row flex-col">
                  <div className={style.twoColFirst}>
                    <p className="mt-5 text-sm">Postal code</p>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.postalCode
                              ? ""
                              : "hidden",
                            "w-4 h-4 absolute top-6",
                            style.errorIcon
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <input
                        className={clsx(
                          methods.formState?.errors?.postalCode?.message
                            ? "error"
                            : "",
                          "mt-2",
                          style.addInput
                        )}
                        type="text"
                        id="postalCode"
                        maxLength={10}
                        {...methods.register("postalCode", {
                          required: "Enter your postal code",
                          pattern: {
                            value:
                              /^(?!.*[dfioquDFIOQU])[a-vxyA-VXYZ][0-9][a-zA-Z] ?[0-9][a-zA-Z][0-9]$/,
                            message: "Enter a valid postal code",
                          },
                        })}
                        onChange={(e) => {
                          methods.setValue("postalCode", e.target.value.toUpperCase());
                        }}
                        onBlur={(e) => {
                          //Update address of real estate asset if living situation is own
                          if (applicant.residentStatus == "own" && application.applicationCategory == applicationCategory.Buy) {
                            updateRealEstateAddress();
                          }
                        }}
                      />
                    </div>
                    {methods.formState?.errors?.postalCode?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.postalCode.message}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="mt-5 text-sm">Country</p>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.country ? "" : "hidden",
                            "w-4 h-4 absolute top-6",
                            style.errorIcon
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <select
                        id="country"
                        className={clsx(
                          methods.formState?.errors?.country ? "error" : "",
                          "mt-2 pt-2.5",
                          style.addInput
                        )}
                        {...methods.register("country", {})}
                        onChange={(e) => {
                          handleCaChange(e);
                        }}
                        onBlur={(e) => {
                          //Update address of real estate asset if living situation is own
                          if (applicant.residentStatus == "own" && application.applicationCategory == applicationCategory.Buy) {
                            updateRealEstateAddress();
                          }
                        }}
                      >
                        <option hidden value="">
                          Country
                        </option>
                        {getCountry}
                      </select>
                    </div>
                    {methods.formState?.errors?.country && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        Select a valid country
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex md:flex-row flex-col w-max mt-5">
                  <div>
                    <p className="text-sm">Living Situation</p>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.residentStatus ? "" : "hidden",
                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[275px]"
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <select
                        id="title"
                        {...methods.register("residentStatus", {
                          required: "Select an option",
                          onChange: residentStatusChangeHandler
                        })}
                        onChange={residentStatusChangeHandler}
                        defaultValue=""
                        className={clsx(
                          "", "mt-2 pt-2.5", style.addInput
                        )}
                      >
                        <option hidden value="">
                          Choose...
                        </option>
                        <option value="own">Own</option>
                        <option value="rent">Rent</option>
                        <option value="liveswithparents">Live with Parents</option>
                        <option value="board">Board</option>
                      </select>
                    </div>
                    {methods.formState?.errors?.residentStatus?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.residentStatus.message}
                      </span>
                    )}
                  </div>
                  <div className="md:ml-4">
                    <p className="text-sm">How long have you lived here?</p>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            "hidden",
                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <input
                        className={clsx(
                          "mt-2", style.residentPeriod
                        )}
                        type="number"
                        min={0}
                        placeholder="year(s)"
                        max={99}
                        id="residentLengthYears"
                        value={residenceYearsText}
                        {...methods.register("residenceYears", {
                          required: "Enter your time at this address.",
                          onChange: residenceYearsChangeHandler
                        })}
                        onChange={residenceYearsChangeHandler}
                      />
                      {residenceYearsText != "" &&
                        <span style={{ position: "absolute", top: "19px", left: residenceYearsText.length > 1 ? "28px" : "22px" }}>year{+residenceYearsText != 1 ? "s" : ""}</span>
                      }
                      <input
                        className={clsx(
                          "mt-2 ml-2", style.residentPeriod
                        )}
                        type="number"
                        min={0}
                        max={11}
                        placeholder="month(s)"
                        id="residentLengthMonths"
                        value={residenceMonthsText}
                        {...methods.register("residenceMonths", {
                          required: "Enter the number of months you lived in your current residence.",
                          onChange: residenceMonthsChangeHandler
                        })}
                        onChange={residenceMonthsChangeHandler}
                      />
                      {residenceMonthsText != "" &&
                        <span style={{ position: "absolute", top: "19px", left: residenceMonthsText.length > 1 ? "201px" : "195px" }}>month{+residenceMonthsText != 1 ? "s" : ""}</span>
                      }
                    </div>
                    {methods.formState?.errors?.residenceYears?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.residenceYears.message}
                      </span>
                    )}
                  </div>
                </div>
                {/* <div className="mt-5 flex flex-col-reverse">
                  <label></label>
                  <input
                    id="mailingIsSame"
                    type="checkbox"
                    value={mailingAddressSame ? "true" : "false"}
                    className="accent"
                    {...methods.register("isMailingAddressSame", {
                      onChange: toggleMailingAddress,
                    })}
                  />
                  <label htmlFor="mailingIsSame">
                    <p className="sub-heading ml-7 -mt-5">
                      Mailing address is the same as home address
                    </p>
                  </label>
                </div> */}
                {!isMailAddressChecked && (
                  <>
                    <div className="flex lg:flex-row flex-col">
                      <div className={style.twoColFirst}>
                        <p className="mt-5 text-sm">Street number</p>
                        <div className="flex relative">
                          <span>
                            <img
                              src="assets/error-icon.svg"
                              className={clsx(
                                methods.formState?.errors?.maStreetNumber
                                  ? ""
                                  : "hidden",
                                "w-4 h-4 absolute top-6",
                                style.errorIcon
                              )}
                              alt="error"
                              height={5}
                              width={5}
                            />
                          </span>
                          <input
                            className={clsx(
                              methods.formState?.errors?.maStreetNumber?.message
                                ? "error"
                                : "",
                              "mt-2",
                              style.addInput
                            )}
                            type="text"
                            id="maStreetNumber"
                            {...methods.register("maStreetNumber", {
                              required: "Enter your street number",
                              pattern: {
                                value: /^[ #a-zA-Z0-9._-]+$/,
                                message: "Enter a valid street number",
                              },
                            })}
                          />
                        </div>
                        {methods.formState?.errors?.maStreetNumber?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                            {methods.formState.errors.maStreetNumber.message}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="mt-5 text-sm">Unit number (optional)</p>
                        <div className="flex relative">
                          <input
                            className={clsx("mt-2", style.addInput)}
                            type="text"
                            id="maUnitNumber"
                            {...methods.register("maUnitNumber", {
                              pattern: {
                                value: /^[ #a-zA-Z0-9._-]+$/,
                                message:
                                  "Enter a valid unit number. Only letters, numbers and _-.# are accepted",
                              },
                            })}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex lg:flex-row flex-col">
                      <div className={style.twoColFirst}>
                        <p className="mt-5 text-sm">Street name</p>
                        <div className="flex relative">
                          <span>
                            <img
                              src="assets/error-icon.svg"
                              className={clsx(
                                methods.formState?.errors?.maStreetName
                                  ? ""
                                  : "hidden",
                                "w-4 h-4 absolute top-6",
                                style.errorIcon
                              )}
                              alt="error"
                              height={5}
                              width={5}
                            />
                          </span>
                          <input
                            className={clsx(
                              methods.formState?.errors?.maStreetName?.message
                                ? "error"
                                : "",
                              "mt-2",
                              style.addInput
                            )}
                            type="text"
                            id="maStreetName"
                            {...methods.register("maStreetName", {
                              required: "Enter your street name",
                              pattern: {
                                value: /^[ #a-zA-ZÀ-Ÿ0-9._-]+$/,
                                message: "Enter a valid street name",
                              },
                            })}
                          />
                        </div>
                        {methods.formState?.errors?.maStreetName?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                            {methods.formState.errors.maStreetName.message}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex lg:flex-row flex-col">
                      <div className={style.twoColFirst}>
                        <p className="mt-5 text-sm">City</p>
                        <div className="flex relative">
                          <span>
                            <img
                              src="assets/error-icon.svg"
                              className={clsx(
                                methods.formState?.errors?.maCityName
                                  ? ""
                                  : "hidden",
                                "w-4 h-4 absolute top-6",
                                style.errorIcon
                              )}
                              alt="error"
                              height={5}
                              width={5}
                            />
                          </span>
                          <input
                            className={clsx(
                              methods.formState?.errors?.maCityName?.message
                                ? "error"
                                : "",
                              "mt-2",
                              style.addInput
                            )}
                            type="text"
                            id="maCityName"
                            {...methods.register("maCityName", {
                              required: "Enter your city name",
                              pattern: {
                                value: /^[ #a-zA-ZÀ-Ÿ0-9._'-]+$/,
                                message: "Enter a valid city name",
                              },
                            })}
                          />
                        </div>
                        {methods.formState?.errors?.maCityName?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                            {methods.formState.errors.maCityName.message}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="mt-5 text-sm">Province</p>
                        <div className="flex relative">
                          <span>
                            <img
                              src="assets/error-icon.svg"
                              className={clsx(
                                methods.formState?.errors?.maProvinceName
                                  ? ""
                                  : "hidden",
                                "w-4 h-4 absolute top-6",
                                style.errorIcon
                              )}
                              alt="error"
                              height={5}
                              width={5}
                            />
                          </span>
                          <select
                            {...methods.register("maProvinceName", {
                              required: "Select your province",
                            })}
                            className={clsx(
                              methods.formState?.errors?.maProvinceName?.message
                                ? "error"
                                : "",
                              "mt-2 pt-2.5",
                              style.addInput
                            )}
                            id="maProvinceName"
                          >
                            <option hidden value="">
                              Province
                            </option>
                            <option value="AB">Alberta</option>
                            <option value="BC">
                              British Columbia
                            </option>
                            <option value="MB">Manitoba</option>
                            <option value="NB">New Brunswick</option>
                            <option value="NL">
                              Newfoundland and Labrador
                            </option>
                            <option value="NS">Nova Scotia</option>
                            <option value="ON">Ontario</option>
                            <option value="PE">
                              Prince Edward Island
                            </option>
                            <option value="QC">Quebec</option>
                            <option value="SK">Saskatchewan</option>
                            <option value="NT">
                              Northwest Territories
                            </option>
                            <option value="NU">Nunavut</option>
                            <option value="YT">Yukon</option>
                          </select>
                        </div>
                        {methods.formState?.errors?.maProvinceName?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                            {methods.formState.errors.maProvinceName.message}
                          </span>
                        )}
                      </div>
                      <div>
                        <div className={style.twoColFirst}>
                          <p className="mt-5 text-sm">Postal code</p>
                          <div className="flex relative">
                            <span>
                              <img
                                src="assets/error-icon.svg"
                                className={clsx(
                                  methods.formState?.errors?.maPostalCode
                                    ? ""
                                    : "hidden",
                                  "w-4 h-4 absolute top-6",
                                  style.errorIcon
                                )}
                                alt="error"
                                height={5}
                                width={5}
                              />
                            </span>
                            <input
                              className={clsx(
                                methods.formState?.errors?.maPostalCode?.message
                                  ? "error"
                                  : "",
                                "mt-2",
                                style.addInput
                              )}
                              type="text"
                              id="maPostalCode"
                              {...methods.register("maPostalCode", {
                                required: "Enter your postal code",
                                pattern: {
                                  value:
                                    /^(?!.*[dfioquDFIOQU])[a-vxyA-VXY][0-9][a-zA-Z] ?[0-9][a-zA-Z][0-9]$/,
                                  message: "Enter a valid postal code",
                                },
                              })}
                            />
                          </div>
                          {methods.formState?.errors?.maPostalCode?.message && (
                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                              {methods.formState.errors.maPostalCode.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex lg:flex-row flex-col">
                      <div className={style.twoColFirst}>
                        <p className="mt-5 text-sm">Country</p>
                        <div className="flex relative">
                          <span>
                            <img
                              src="assets/error-icon.svg"
                              className={clsx(
                                methods.formState?.errors?.maCountry
                                  ? ""
                                  : "hidden",
                                "w-4 h-4 absolute top-6",
                                style.errorIcon
                              )}
                              alt="error"
                              height={5}
                              width={5}
                            />
                          </span>
                          <select
                            id="maCountry"
                            className={clsx(
                              methods.formState?.errors?.maCountry
                                ? "error"
                                : "",
                              "mt-2 pt-2.5",
                              style.addInput
                            )}
                            {...methods.register("maCountry", {
                            })}
                          >
                            <option hidden value="">
                              Country
                            </option>
                            {getCountry}
                          </select>
                        </div>
                        {methods.formState?.errors?.maCountry && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                            Select a valid country
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
                <div className="flex max-sm:hidden">
                  <div className="mb-5">
                    <button
                      className="primary mt-5 w-169 sm:w-28 h-11 px-6"
                      type="button"
                      onClick={() => {
                        closeEdit(true);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="mb-5">
                    <button
                      type="submit"
                      className="primary mt-5 w-169 sm:w-28 h-11 px-6 mx-5"
                      disabled={isSaving}
                    >
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                      Save
                    </button>
                  </div>
                </div>
                <div className="flex sm:hidden">
                  <div className="mb-5">
                    <button
                      className="primary mt-5 w-169 sm:w-28 h-11 px-6"
                      type="button"
                      onClick={() => closeEdit(true)}
                      style={{ minWidth: 90 }}
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="mb-5">
                    <button
                      type="submit"
                      className="primary mt-5 w-169 sm:w-28 h-11 px-6 mx-5"
                      disabled={isSaving}
                      style={{ minWidth: 90 }}
                    >
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                      Save
                    </button>
                  </div>
                </div>
              </form>
            </motion.div>
          )}
          {/* TODO: need to implement in next phase */}
          {/* <div className="flex md:flex-row flex-col">
          <div className={style.twoColFirst}>
            <p className="mt-5 text-sm">Would you like a debit card</p>
            <h5 className="mt-3">Yes</h5>
          </div>
        </div> */}
        </AnimatePresence>
      </div>

    </>
  );
}
